/**
 * The Space weather page's answers (docs/specs/space-weather.md §4.4, §4.5; decisions 687, 694): what is asked, how
 * often and how each answer is kept is the domain's, shared with Teslyn Field (`space-weather-view.ts`, R115). Each
 * answer is kept in this browser's storage with its time (`teslyn.spaceweather.v1.<organisation>.…`), so the page
 * opens on a phone with no signal showing the last values and their ages. Asked only while the page is open, checked
 * each minute.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  heldEntries,
  isDue,
  runSpaceWeatherRound,
  swKey,
  type KeyValue,
  type ProxyClient,
  type SwEntry,
  type SwRequest,
} from '@teslyn/domain';

/** This browser's storage, or null where it cannot be used (a private window that refuses it). */
export function browserStore(): KeyValue | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** How far the running round has got: the sources answered (failed ones counted) of the ones it asks. */
export interface SwProgress {
  readonly answered: number;
  readonly total: number;
}

export interface SpaceWeatherData {
  readonly entries: Readonly<Record<string, SwEntry>>;
  readonly busy: boolean;
  /** The running round's count (R168), null between rounds. */
  readonly progress: SwProgress | null;
  /** How many rounds that asked at least one source have ended since the page opened (the banner's "finished"). */
  readonly rounds: number;
  /** The last round reached nothing: no signal, or signed out. */
  readonly offline: boolean;
  /** Ask everything again now (Refresh). */
  readonly refresh: () => void;
}

export function useSpaceWeatherData(opts: {
  readonly tenantId: string;
  readonly client: ProxyClient;
  readonly requests: readonly SwRequest[];
  readonly store?: KeyValue | null;
  readonly now?: () => number;
  readonly tickMs?: number;
}): SpaceWeatherData {
  const { tenantId, client, requests } = opts;
  const store = opts.store === undefined ? browserStore() : opts.store;
  const now = opts.now ?? Date.now;
  const tickMs = opts.tickMs ?? 60_000;
  const reqKey = requests.map(swKey).join('|');
  const initial = useMemo(
    () => heldEntries(store, tenantId, requests),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the request list's content
    [tenantId, reqKey],
  );
  const [entries, setEntries] = useState<Readonly<Record<string, SwEntry>>>(initial);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<SwProgress | null>(null);
  const [rounds, setRounds] = useState(0);
  const [offline, setOffline] = useState(false);
  const live = useRef(entries);
  live.current = entries;
  const running = useRef(false);

  useEffect(() => {
    setEntries(initial);
    live.current = initial;
  }, [initial]);

  const round = useCallback(
    async (force: boolean): Promise<void> => {
      if (running.current) return;
      const t0 = now();
      if (!force && !requests.some((r) => isDue(r, live.current[swKey(r)], t0))) return;
      running.current = true;
      setBusy(true);
      try {
        const r = await runSpaceWeatherRound({
          tenantId,
          client,
          requests,
          entries: live.current,
          store,
          now,
          force,
          onEntry: (key, entry) => {
            live.current = { ...live.current, [key]: entry };
            setEntries(live.current);
          },
          onProgress: (answered, total) => setProgress({ answered, total }),
        });
        if (r.asked > 0) {
          setOffline(r.offline);
          setRounds((n) => n + 1);
        }
      } finally {
        running.current = false;
        setProgress(null);
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by content
    [tenantId, client, reqKey],
  );

  useEffect(() => {
    void round(false);
    const t = setInterval(() => void round(false), tickMs);
    return () => clearInterval(t);
  }, [round, tickMs]);

  return { entries, busy, progress, rounds, offline, refresh: () => void round(true) };
}
